import BaseApiClient from '../BaseApiClient';
import { TypeInterface, WealthSourceOption } from '@interfaces/Type.interface';
import { ListDataParams } from '@api-client/types';
import { OptionType } from '@hooks/api/useOptions';
import ProductDetailsInterface from '@interfaces/ProductDetails.interface';

export class ApiTypes extends BaseApiClient {
  /**
   * Get all items
   */
  async getContactTypes(
    type: 'individual' | 'company' | 'task-client',
    listData?: ListDataParams
  ): Promise<Array<TypeInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .getList(`types/${type}`, listData)
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getInvestmentOptions(
    optionType: string
  ): Promise<Array<TypeInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .getList(`investment-options/${optionType}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getOptions(
    optionType: OptionType,
    searchInput?: string,
    options?: {
      apiVersion?: string;
      pipelineTypeFilter?: string;
      businessUnitId?: number;
    }
  ): Promise<Array<TypeInterface>> {
    const headers = options?.apiVersion ? { Version: options.apiVersion } : {};

    const queryParams: string[] = [];

    if (searchInput) {
      queryParams.push(`filter[name]=${searchInput}`);
    }

    if (options?.pipelineTypeFilter && optionType === 'pipelines') {
      queryParams.push(`filter[pipeline_type]=${options.pipelineTypeFilter}`);
    }

    if (options?.businessUnitId != null && optionType === 'pipelines') {
      queryParams.push(`filter[business_unit_id]=${options.businessUnitId}`);
    }
    const queryString =
      queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

    return new Promise((resolve, reject) => {
      this.client
        .getList(`${optionType}${queryString}`, undefined, { headers })
        .then((res) => {
          if (res.data) {
            const rawData = res.data.data as unknown[];
            if (
              optionType === 'data-lists' &&
              Array.isArray(rawData) &&
              rawData.length > 0
            ) {
              const first = rawData[0] as {
                data_list_values?: Array<{ id: number; label: string }>;
                options?: Array<{ id: number; label: string }>;
              };
              const values =
                first.data_list_values ??
                first.options ??
                ([] as Array<{ id: number; label: string }>);
              resolve(
                values.map(
                  (v) => ({ id: v.id, name: v.label } as TypeInterface)
                )
              );
            } else {
              resolve(res.data.data);
            }
          } else {
            reject('Invalid response');
          }
        });
    });
  }
  async getWealthSources(): Promise<Array<WealthSourceOption>> {
    return new Promise((resolve, reject) => {
      this.client
        .getList('data-lists?filter[group]=wealth-source')
        .then((res) => {
          resolve(res.data.data);
        })
        .catch((err) => reject(err));
    });
  }
  async getCountries(exclude?: string): Promise<Array<TypeInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .getList(`hub/countries${exclude ? exclude : ''}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getInvestmentProducts(): Promise<Array<ProductDetailsInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .getList('investment-products')
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
}

export { apiTypesQueryKeys } from './query-keys';
export type {
  ApiTypesContactTypesParams,
  ApiTypesGetOptionsParams
} from './query-keys';
export {
  useApiTypesContactTypesQuery,
  useApiTypesCountriesQuery,
  useApiTypesInvestmentOptionsQuery,
  useApiTypesInvestmentProductsQuery,
  useApiTypesOptionsQuery,
  useApiTypesWealthSourcesQuery
} from './use-api-types-queries';

